import { ClientId, NoteEntityId, NoteId, TimeEntryId } from "@sp-crm/core";
import {
    useQuery as useTrpcQuery,
    useQueryClient as useTrpcQueryClient,
} from "@tanstack/react-query";
import { ActivityFeed } from "components/activity-feed/activity-feed";
import { ActivityFeedNote } from "components/activity-feed/activity-feed-note";
import { ActivityFeedTimeEntry } from "components/activity-feed/activity-feed-time-entry";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ActivityEntityType } from "components/entity-activity/types";
import { trpc } from "components/rpc/trpc";
import {
    GetNotesForEntityQuery,
    UpdateNoteMutation,
    useGetNotesForEntityQuery,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { NotesInput } from "./notes-input";

type NoteRow = GetNotesForEntityQuery["getNotesForEntity"][0];

interface ModernNotesProps {
    entityId: NoteEntityId;
    entityType?: ActivityEntityType;
}

export const ModernNotes: React.FC<ModernNotesProps> = props => {
    const { entityId, entityType } = props;
    const query = useGetNotesForEntityQuery({ entityId });
    const queryClient = useQueryClient();
    const trpcQueryClient = useTrpcQueryClient();
    const [localDeletes, setLocalDeletes] = React.useState<NoteId[]>([]);
    const [deletedTimeEntryIds, setDeletedTimeEntryIds] = React.useState<TimeEntryId[]>(
        [],
    );

    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();
    const isClient = entityType === "Client";
    const timeTrackingEnabled =
        isClient && trackTimeFeature && !!settings.trackTimeEnabled;

    const trpcHooks = trpc.useTRPC();
    const timeEntriesQuery = useTrpcQuery({
        ...trpcHooks.timeEntries.listByClient.queryOptions({
            clientId: entityId as ClientId,
            limit: 200,
        }),
        enabled: timeTrackingEnabled,
    });

    const invalidateTimeEntries = React.useCallback(() => {
        trpcQueryClient.invalidateQueries({ queryKey: [["timeEntries"]] });
    }, [trpcQueryClient]);

    const handleNoteChanged = React.useCallback(
        (note: NoteRow) => {
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        let replaced = false;
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n => {
                            if (n._id === note._id) {
                                replaced = true;
                                return note;
                            }
                            return n;
                        });

                        if (!replaced) {
                            draft.getNotesForEntity.unshift(note);
                        }
                    });
                },
            );
        },
        [queryClient, entityId],
    );

    const handleNoteDeleted = React.useCallback((noteId: NoteId) => {
        setLocalDeletes(d => [...d, noteId]);
    }, []);

    const handleNoteUpdated = React.useCallback(
        (note: UpdateNoteMutation["updateNote"]) => {
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n => {
                            if (n._id === note._id) {
                                return note;
                            }
                            return n;
                        });
                    });
                },
            );
        },
        [queryClient, entityId],
    );

    const handleTimeEntryDeleted = React.useCallback((entryId: TimeEntryId) => {
        setDeletedTimeEntryIds(prev => [...prev, entryId]);
    }, []);

    return (
        <QueryRenderer name="ModernNotes" query={query}>
            {data => {
                const notes = data.getNotesForEntity
                    .filter(n => !n.isDraft && !localDeletes.includes(n._id))
                    .map(n => ({
                        kind: "note" as const,
                        key: n._id,
                        timestamp: new Date(n.dateAdded).getTime(),
                        note: n,
                    }));

                const timeEntries = (timeEntriesQuery.data ?? [])
                    .filter(e => !deletedTimeEntryIds.includes(e.id))
                    .map(e => ({
                        kind: "timeEntry" as const,
                        key: e.id,
                        timestamp: new Date(e.startTime).getTime(),
                        entry: e,
                    }));

                const feed = [...notes, ...timeEntries].sort(
                    (a, b) => b.timestamp - a.timestamp,
                );

                return (
                    <div>
                        <NotesInput
                            refetch={query.refetch}
                            entityId={entityId}
                            initial={data.getNotesForEntity
                                .filter(n => !localDeletes.includes(n._id))
                                .sort(
                                    (a, b) =>
                                        new Date(b.dateAdded).getTime() -
                                        new Date(a.dateAdded).getTime(),
                                )}
                            onNoteChanged={handleNoteChanged}
                        />
                        <ActivityFeed>
                            {feed.map(item =>
                                item.kind === "note" ? (
                                    <ActivityFeedNote
                                        entityId={entityId}
                                        refetch={query.refetch}
                                        key={item.key}
                                        note={item.note}
                                        onDeleted={handleNoteDeleted}
                                        onUpdated={handleNoteUpdated}
                                    />
                                ) : (
                                    <ActivityFeedTimeEntry
                                        key={item.key}
                                        entry={{
                                            id: item.entry.id,
                                            userId: item.entry.userId,
                                            startTime: item.entry.startTime,
                                            durationMinutes: item.entry.durationMinutes,
                                            note: item.entry.note,
                                        }}
                                        onSaved={invalidateTimeEntries}
                                        onDeleted={handleTimeEntryDeleted}
                                    />
                                ),
                            )}
                        </ActivityFeed>
                    </div>
                );
            }}
        </QueryRenderer>
    );
};
